import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";
import moment from "moment";

// Send interest request (Tenant -> Landlord)
export const SendFlatInterestRequest = createAsyncThunk(
  "flatInterest/sendRequest",
  async ({ requestData }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/flat-interest/send`,
        requestData
      );
      return response;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to send request."
      );
    }
  }
);

// Get all interest requests for landlord
export const GetLandlordInterestRequests = createAsyncThunk(
  "flatInterest/getLandlordRequests",
  async ({ landlordId }) => {
    const response = await axios.get(
      `${BASE_URL}/flat-interest/landlord/${landlordId}`
    );
    return response;
  }
);

// Get all interest requests for tenant
export const GetTenantInterestRequests = createAsyncThunk(
  "flatInterest/getTenantRequests",
  async ({ tenantId }) => {
    const response = await axios.get(
      `${BASE_URL}/flat-interest/tenant/${tenantId}`
    );
    return response;
  }
);

// Update request status (Accept/Reject by landlord)
export const UpdateInterestRequestStatus = createAsyncThunk(
  "flatInterest/updateStatus",
  async ({
    requestId,
    status,
    message,
    paymentMethod,
    paymentAmount,
    paymentDate,
    transactionId,
    paymentStatus,
    due_date,
  }) => {
    const response = await axios.put(
      `${BASE_URL}/flat-interest/update-status/${requestId}`,
      {
        status,
        message,
        due_date: moment(due_date).format("DD-MM-YYYY"),
        paymentMethod,
        paymentAmount,
        paymentDate,
        transactionId,
        paymentStatus, // "verifying" = waiting for landlord verification, "verified" = landlord verified, "failed" = rejected
      }
    );
    return response;
  }
);

// Cancel request (by tenant) — for a still-open "pending" or "hold" request,
// before any payment. Withdrawing a paid booking is CancelBooking instead.
export const CancelInterestRequest = createAsyncThunk(
  "flatInterest/cancel",
  async ({ requestId }) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/flat-interest/cancel/${requestId}`
      );
      return response;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to cancel request."
      );
    }
  }
);

// What the tenant gets back if they cancel right now. Always asked of the
// server — the refund ladder is enforced there, and a second copy of the
// percentages in the browser is how the promise and the payout drift apart.
export const GetRefundPreview = createAsyncThunk(
  "flatInterest/refundPreview",
  async ({ requestId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/flat-interest/refund-preview/${requestId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to calculate refund" }
      );
    }
  }
);

// The tenant's one allowed ask for more time after missing the payment
// deadline. The landlord still decides — this only raises the flag and
// notifies them; approval happens through the existing Accept-with-new-date
// flow.
export const RequestPaymentExtension = createAsyncThunk(
  "flatInterest/requestExtension",
  async ({ requestId, tenantId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/flat-interest/request-extension/${requestId}`,
        { tenantId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to request extension" }
      );
    }
  }
);

// The tenant telling their owner they have moved in. The owner cannot see
// this for themselves, so the tenant reports it — but it only sends the alert,
// it does not start the tenancy.
export const MarkMovedIn = createAsyncThunk(
  "flatInterest/markMovedIn",
  async ({ requestId, tenantId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/flat-interest/mark-moved-in/${requestId}`,
        { tenantId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to notify your owner" }
      );
    }
  }
);

// Owner confirms the tenant actually moved in. This is what starts the
// tenancy — verifying the payment only books the flat.
export const ConfirmMoveIn = createAsyncThunk(
  "flatInterest/confirmMoveIn",
  async ({ requestId, landlordId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/flat-interest/confirm-move-in/${requestId}`,
        { landlordId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to confirm move-in" }
      );
    }
  }
);

// Tenant cancels a paid booking before move-in.
export const CancelBooking = createAsyncThunk(
  "flatInterest/cancelBooking",
  async ({ requestId, tenantId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/flat-interest/cancel-booking/${requestId}`,
        { tenantId, reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to cancel booking" }
      );
    }
  }
);

const FlatInterestRequest = createSlice({
  name: "flatInterest",
  initialState: {
    loading: false,
    requests: [],
    myRequests: [], // For tenant's own requests
    currentRequest: null,
    error: "",
    success: false,
  },
  reducers: {
    resetInterestState: (state) => {
      state.loading = false;
      state.error = "";
      state.success = false;
      state.currentRequest = null;
    },
    clearError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    // Send Request
    builder.addCase(SendFlatInterestRequest.pending, (state) => {
      state.loading = true;
      state.error = "";
      state.success = false;
    });
    builder.addCase(SendFlatInterestRequest.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.currentRequest = action.payload?.data?.data;
    });
    builder.addCase(SendFlatInterestRequest.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || action.error.message;
      state.success = false;
    });

    // Get Landlord Requests
    builder.addCase(GetLandlordInterestRequests.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetLandlordInterestRequests.fulfilled, (state, action) => {
      state.loading = false;
      state.requests = action.payload?.data?.data || [];
    });
    builder.addCase(GetLandlordInterestRequests.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Get Tenant Requests
    builder.addCase(GetTenantInterestRequests.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetTenantInterestRequests.fulfilled, (state, action) => {
      state.loading = false;
      state.myRequests = action.payload?.data?.data || [];
    });
    builder.addCase(GetTenantInterestRequests.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Update Status
    builder.addCase(UpdateInterestRequestStatus.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(UpdateInterestRequestStatus.fulfilled, (state, action) => {
      state.loading = false;
      // Update the request in the list
      const updatedRequest = action.payload?.data?.data;
      if (updatedRequest) {
        state.requests = state.requests.map((req) =>
          req._id === updatedRequest._id ? updatedRequest : req
        );
      }
    });
    builder.addCase(UpdateInterestRequestStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Cancel Request
    builder.addCase(CancelInterestRequest.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(CancelInterestRequest.fulfilled, (state, action) => {
      state.loading = false;
      const cancelledId = action.meta.arg.requestId;
      state.myRequests = state.myRequests.filter(
        (req) => req._id !== cancelledId
      );
    });
    builder.addCase(CancelInterestRequest.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  },
});

export const { resetInterestState, clearError } = FlatInterestRequest.actions;
export default FlatInterestRequest.reducer;
